import type {
  AppraisalMetadata,
  AppraisalOfferV2,
  AppraisalV2,
  AssigneeType,
  Auction,
  Comment,
  Confirmation,
  ConfirmationHistoryEntry,
  ConsolidatedInquiry,
  CustomerLead,
  DeliveryStage,
  EverUser,
  GenericVehicle,
  InstantOfferV2,
  KeyIndicators,
  PersonalDetails,
  PreviousReservation,
  ProtectionPlanName,
  ProtectionPlanTermLength,
  ProtectionPlanTermMiles,
  ProtectionPlanType,
  RapidReconStep,
  Reply,
  Seller,
  SMSAgentMessage,
  TaskableType,
  TaskStatus,
  TaskTemplate,
  TaskV2,
  TradeStatus,
  VehicleBody,
  VehicleChargingPlug,
  VehicleHistory,
  VehicleImage,
  VehicleImageCategory,
  VehicleListingPurchaseStatus,
  VehicleListingStatus,
  VehicleLocation,
  VehicleSeatingCapacity,
} from '@/models/types';
import { ConfirmationField } from '@/models/types';
import type { JSONContent } from '@tiptap/core';
import { makeObservable, observable } from 'mobx';
import type {
  Address,
  AssignedTask,
  Audit,
  AuditLog,
  CoreDeal,
  Customer,
  DealRisk,
  DeliveryPreference,
  EmojiReaction,
  Favorite,
  FileMetadata,
  FundingMethod,
  Hub,
  Lender,
  Notification,
  Payment,
  PaymentMethod,
  PaymentType,
  PriceBreakdown,
  Priority,
  ProtectionPlan,
  ResellType,
  Role,
  Stage,
  Status,
  Task,
  Trade,
  TradeCurrentLocation,
  TradeIn,
  User,
  VehicleFromIndexer,
  VehicleListing,
} from './types';

/**
 * Notification model
 */
export abstract class NotificationModel {
  id: string;
  @observable deal_id: string;
  @observable triggered_by_user_id: string; // The user who triggered the notification
  @observable user_id: string; // Apparently this is the user being mentioned
  @observable message: string;
  @observable read_at?: string | null;
  @observable type: 'assignment' | 'mention';
  // TODO (jimmy): There's a better way to type this so that we know
  // which `metadata` keys are valid for a given `type`
  @observable created_at: string;
  @observable metadata?: {
    comment_id?: string;
    reply_id?: string;
  };

  constructor(notification: Notification) {
    this.id = notification.id;
    this.deal_id = notification.deal_id;
    this.triggered_by_user_id = notification.triggered_by_user_id;
    this.user_id = notification.user_id;
    this.message = notification.message;
    this.read_at = notification.read_at;
    this.type = notification.type;
    this.metadata = notification.metadata;
    this.created_at = notification.created_at;
  }
}

/**
 * Favorite model
 */
export abstract class FavoriteModel {
  id: string;
  deal_id: string;
  user_id: string;
  hub_id: string;

  constructor(favorite: Favorite) {
    this.id = favorite.id;
    this.deal_id = favorite.deal_id;
    this.user_id = favorite.user_id;
    this.hub_id = favorite.hub_id;
  }
}

/**
 * Hub model
 */
export abstract class HubModel {
  id: string;
  name: string;
  path_identifier: string;
  code: string;

  constructor(hub: Hub) {
    this.id = hub.id;
    this.name = hub.name;
    this.path_identifier = hub.path_identifier;
    this.code = hub.code;
  }
}

/**
 * Deal model
 */
export abstract class CoreDealModel {
  id: string;
  @observable vehicle_listing_id: string;
  created_at: string;
  @observable last_updated: string;
  @observable archived_at: string | null;
  short_id: string;
  @observable status: Status;
  @observable stage: Stage;

  @observable cdk_id: number;
  @observable hub_path_identifier: string;
  @observable delivery_preference: DeliveryPreference;
  @observable delivery_date: string | null;
  @observable pickup_date: string | null;
  @observable date_added_to_customer_delivery_queue: string | null;
  @observable favorited: boolean;
  @observable priority: Priority;
  @observable funding_method: FundingMethod;
  @observable risk: DealRisk | null;
  @observable ai_summary:
    | {
        bullet: string;
        date_time: string;
        subject: string;
      }[]
    | null;
  @observable sms_conversation: string[] | null;
  @observable agent_convo_paused: boolean;
  @observable vehicle_delivery_odometer: number | null;
  @observable rapid_recon_step: RapidReconStep | null;
  @observable delivery_stage: DeliveryStage | null;
  @observable vehicle_current_location_id: string | null;
  @observable sms_flow: string | null;
  @observable sms_flow_step: string | null;

  // assignees
  @observable finance_manager_id?: string | null;
  @observable primary_sales_rep_id?: string | null;
  @observable secondary_sales_rep_id?: string | null;
  @observable lender_id?: string | null;

  constructor(deal: CoreDeal) {
    makeObservable(this);
    this.id = deal.id;
    this.vehicle_listing_id = deal.vehicle_listing_id;
    this.created_at = deal.created_at;
    this.last_updated = deal.last_updated;
    this.archived_at = deal.archived_at;
    this.short_id = deal.short_id;
    this.status = deal.status;
    this.stage = deal.stage;
    this.cdk_id = deal.cdk_id;
    this.hub_path_identifier = deal.hub_path_identifier;
    this.delivery_preference = deal.delivery_preference;
    this.delivery_date = deal.delivery_date;
    this.pickup_date = deal.pickup_date;
    this.date_added_to_customer_delivery_queue =
      deal.date_added_to_customer_delivery_queue;
    this.favorited = deal.favorited;
    this.priority = deal.priority;
    this.risk = deal.risk;
    this.finance_manager_id = deal.finance_manager_id;
    this.primary_sales_rep_id = deal.primary_sales_rep_id;
    this.secondary_sales_rep_id = deal.secondary_sales_rep_id;
    this.funding_method = deal.funding_method;
    this.lender_id = deal.lender_id;
    this.ai_summary = deal.ai_summary;
    this.sms_conversation = deal.sms_conversation;
    this.agent_convo_paused = deal.agent_convo_paused;
    this.vehicle_delivery_odometer = deal.vehicle_delivery_odometer;
    this.rapid_recon_step = deal.rapid_recon_step;
    this.vehicle_current_location_id = deal.vehicle_current_location_id;
    this.delivery_stage = deal.delivery_stage;
    this.sms_flow = deal.sms_flow;
    this.sms_flow_step = deal.sms_flow_step;
  }
}

/**
 * User model
 */
export abstract class UserModel {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: Role;
  avatar: string;
  slack_connected?: boolean;
  username: string;
  slack_id?: string;
  slack_access_token?: string;

  constructor(user: User) {
    this.id = user.id;
    this.first_name = user.first_name;
    this.last_name = user.last_name;
    this.email = user.email;
    this.role = user.role;
    this.avatar = user.avatar;
    this.slack_connected =
      user.slack_connected ??
      (user.slack_id !== undefined && user.slack_access_token !== undefined);
    this.username = user.username;
    this.slack_id = user.slack_id;
    this.slack_access_token = user.slack_access_token;
  }
}

export abstract class ConfirmationModel {
  id: string;
  field_key: ConfirmationField;
  confirmed: boolean;
  most_recent_entry_id: string;
  is_manually_toggled: boolean;

  constructor(confirmation: Confirmation) {
    this.id = confirmation.id;
    this.field_key = confirmation.field_key;
    this.confirmed = confirmation.confirmed;
    this.most_recent_entry_id = confirmation.most_recent_entry_id;
    this.is_manually_toggled = confirmation.is_manually_toggled;
  }
}

export abstract class ConfirmationHistoryModel {
  id: string;
  confirmation_id: string;
  user_id: string;
  value: string;
  created_at: string;

  constructor(confirmationHistoryEntry: ConfirmationHistoryEntry) {
    this.id = confirmationHistoryEntry.id;
    this.confirmation_id = confirmationHistoryEntry.confirmation_id;
    this.user_id = confirmationHistoryEntry.user_id;
    this.value = confirmationHistoryEntry.value;
    this.created_at = confirmationHistoryEntry.created_at;
  }
}

/**
 * EmojiReaction model: all models that have an emoji reaction will extend this class
 */
export abstract class EmojiReactionModel {
  id: string;
  user_id: string;
  emoji: string;
  created_at: string;

  constructor(reaction: EmojiReaction) {
    this.id = reaction.id;
    this.user_id = reaction.user_id;
    this.emoji = reaction.emoji;
    this.created_at = reaction.created_at;
  }
}
export abstract class CommentModel {
  id: string;
  created_at: string;
  updated_at: string;

  user_id: string;
  content: JSONContent;
  text: string;
  edited: boolean;

  constructor(comment: Comment) {
    this.id = comment.id;
    this.created_at = comment.created_at;
    this.updated_at = comment.updated_at;
    this.user_id = comment.user_id;
    this.content = comment.content;
    this.text = comment.text;
    this.edited = comment.edited;
  }
}

export abstract class ReplyModel {
  id: string;
  comment_id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  content: JSONContent;
  edited: boolean;
  text: string;
  mentions: User['id'][];

  constructor(reply: Reply) {
    this.id = reply.id;
    this.comment_id = reply.comment_id;
    this.created_at = reply.created_at;
    this.updated_at = reply.updated_at;
    this.user_id = reply.user_id;
    this.content = reply.content;
    this.edited = reply.edited;
    this.text = reply.text;
    this.mentions = reply.mentions;
  }
}

/**
 * Customer model
 */
export type CustomerType = 'primary' | 'co_registrant';
export abstract class CustomerModel {
  id: string;
  @observable customer_type: CustomerType;
  @observable deal_id: string;
  @observable full_name: string;
  @observable email: string;
  @observable mobile_number: string;

  constructor(customer: Customer) {
    this.id = customer.id;
    this.customer_type = customer.customer_type;
    this.deal_id = customer.deal_id;
    this.full_name = customer.full_name;
    this.email = customer.email;
    this.mobile_number = customer.mobile_number;
  }
}

export abstract class CustomerAddressModel {
  id: string;
  customer_id: string;
  address_type: 'registration' | 'delivery';
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;

  constructor(address: Address) {
    this.id = address.id;
    this.customer_id = address.customer_id;
    this.address_type = address.address_type;
    this.address_line_1 = address.address_line_1;
    this.address_line_2 = address.address_line_2;
    this.city = address.city;
    this.state = address.state;
    this.postal_code = address.postal_code;
  }
}

/**
 * Protection plan model
 */
export abstract class ProtectionPlanModel {
  id: string;
  name: string;
  key: string;
  type: string;
  term_length: number;
  term_miles: number;
  monthly_price: number;
  price: number;

  constructor(protectionPlan: ProtectionPlan) {
    this.id = protectionPlan.id;
    this.name = protectionPlan.name;
    this.key = protectionPlan.key;
    this.type = protectionPlan.type;
    this.term_length = protectionPlan.term_length;
    this.term_miles = protectionPlan.term_miles;
    this.monthly_price = protectionPlan.monthly_price;
    this.price = protectionPlan.price;
  }
}

/**
 * Lender model
 */
export abstract class LenderModel {
  id: string;
  name: string;
  email: string;
  phone: string;
  lender_type: 'bank' | 'credit_union';

  constructor(lender: Lender) {
    this.id = lender.id;
    this.name = lender.name;
    this.email = lender.email;
    this.phone = lender.phone;
    this.lender_type = lender.lender_type;
  }
}

export abstract class AuctionModel {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;

  constructor(auction: Auction) {
    this.id = auction.id;
    this.created_at = auction.created_at;
    this.updated_at = auction.updated_at;
    this.name = auction.name;
  }
}

/**
 * Vehicle location model
 */
export class VehicleLocationModel {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;

  constructor(vehicleLocation: VehicleLocation) {
    makeObservable(this, {
      id: observable,
      created_at: observable,
      updated_at: observable,
      name: observable,
    });

    this.id = vehicleLocation.id;
    this.created_at = vehicleLocation.created_at;
    this.updated_at = vehicleLocation.updated_at;
    this.name = vehicleLocation.name;
  }
}

/**
 * Price breakdown model
 */
export abstract class PriceBreakdownModel {
  id: string;
  deal_id: string;
  subtotal: number;
  total: number;
  sales_tax: number;
  registration_fee: number;
  documentation_fee: number;
  title_fee: number;
  delivery_fee: number;
  locked: boolean;

  constructor(priceBreakdown: PriceBreakdown) {
    this.id = priceBreakdown.id;
    this.deal_id = priceBreakdown.deal_id;
    this.subtotal = priceBreakdown.subtotal;
    this.total = priceBreakdown.total;
    this.sales_tax = priceBreakdown.sales_tax;
    this.registration_fee = priceBreakdown.registration_fee;
    this.documentation_fee = priceBreakdown.documentation_fee;
    this.title_fee = priceBreakdown.title_fee;
    this.delivery_fee = priceBreakdown.delivery_fee;
    this.locked = priceBreakdown.locked;
  }
}

/**
 * File metadata model
 */
export abstract class FileMetadataModel {
  id: string;
  deal_id: string;
  name: string;
  url: string;
  size: number;
  type: string;

  constructor(fileMetadata: FileMetadata) {
    this.id = fileMetadata.id;
    this.deal_id = fileMetadata.deal_id;
    this.name = fileMetadata.name;
    this.url = fileMetadata.url;
    this.size = fileMetadata.size;
    this.type = fileMetadata.type;
  }
}

/**
 * Payment model
 */
export abstract class PaymentModel {
  id: string;
  deal_id: string;
  @observable amount: number;
  @observable intent_date: string;
  @observable sent_date: string | null;
  @observable received_date: string | null;
  @observable confirmed_date: string | null;
  @observable refunded_date: string | null;
  @observable receipt_uploaded_by_id: string | null;
  @observable receipt_file_url: string | null;
  @observable receipt_file_name: string | null;
  @observable receipt_file_type: string | null;
  @observable receipt_file_size: number | null;
  @observable method: PaymentMethod;
  @observable type: PaymentType;
  @observable is_full_payment: boolean;
  @observable created_by_id: User['id'];
  @observable created_at: string;
  @observable updated_at: string | null;
  @observable deleted_at: string | null;

  constructor(payment: Payment) {
    this.id = payment.id;
    this.deal_id = payment.deal_id;
    this.amount = payment.amount;
    this.intent_date = payment.intent_date;
    this.sent_date = payment.sent_date;
    this.received_date = payment.received_date;
    this.confirmed_date = payment.confirmed_date;
    this.refunded_date = payment.refunded_date;
    this.receipt_uploaded_by_id = payment.receipt_uploaded_by_id;
    this.receipt_file_url = payment.receipt_file_url;
    this.receipt_file_name = payment.receipt_file_name;
    this.receipt_file_type = payment.receipt_file_type;
    this.receipt_file_size = payment.receipt_file_size;
    this.method = payment.method;
    this.type = payment.type;
    this.is_full_payment = payment.is_full_payment;
    this.created_by_id = payment.created_by_id;
    this.created_at = payment.created_at;
    this.updated_at = payment.updated_at;
    this.deleted_at = payment.deleted_at;
  }
}

/**
 * Trade in model
 */
export abstract class TradeInModel {
  id: string;
  deal_id: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  condition: string;
  paid_off: boolean;
  lienholder: string;
  state: string;
  postal_code: string;
  number_of_keys: number;
  asking_price: number;
  exterior_color: string;
  interior_color: string;
  offer: number;
  resell_type: ResellType;

  constructor(tradeIn: TradeIn) {
    this.id = tradeIn.id;
    this.deal_id = tradeIn.deal_id;
    this.vin = tradeIn.vin;
    this.make = tradeIn.make;
    this.model = tradeIn.model;
    this.trim = tradeIn.trim;
    this.year = tradeIn.year;
    this.mileage = tradeIn.mileage;
    this.condition = tradeIn.condition;
    this.paid_off = tradeIn.paid_off;
    this.lienholder = tradeIn.lienholder;
    this.state = tradeIn.state;
    this.postal_code = tradeIn.postal_code;
    this.number_of_keys = tradeIn.number_of_keys;
    this.asking_price = tradeIn.asking_price;
    this.exterior_color = tradeIn.exterior_color;
    this.interior_color = tradeIn.interior_color;
    this.offer = tradeIn.offer;
    this.resell_type = tradeIn.resell_type;
  }
}

/**
 * Trade in model
 */
export abstract class TradeModel {
  id: string;
  short_id: string;
  created_at: string;
  updated_at: string;
  created_by_id: string;
  deal_id: string;
  full_name: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  condition: string;
  paid_off: boolean;
  lienholder: string;
  state: string;
  postal_code: string;
  number_of_keys: number;
  asking_price: number;
  exterior_color: string;
  interior_color: string;
  offer: number;
  resell_type: ResellType;
  status: TradeStatus;
  title_present: boolean;
  payoff_amount: number;
  payoff_expiration: string | null;
  appraised_value: number;
  ever_location: string;
  current_location: TradeCurrentLocation;
  auctions: string[] | null; // Array of auction IDs

  constructor(trade: Trade) {
    this.id = trade.id;
    this.short_id = trade.short_id;
    this.created_at = trade.created_at;
    this.updated_at = trade.updated_at;
    this.created_by_id = trade.created_by_id;
    this.deal_id = trade.deal_id;
    this.full_name = trade.full_name;
    this.vin = trade.vin;
    this.make = trade.make;
    this.model = trade.model;
    this.trim = trade.trim;
    this.year = trade.year;
    this.mileage = trade.mileage;
    this.condition = trade.condition;
    this.paid_off = trade.paid_off;
    this.lienholder = trade.lienholder;
    this.state = trade.state;
    this.postal_code = trade.postal_code;
    this.number_of_keys = trade.number_of_keys;
    this.asking_price = trade.asking_price;
    this.exterior_color = trade.exterior_color;
    this.interior_color = trade.interior_color;
    this.offer = trade.offer;
    this.resell_type = trade.resell_type;
    this.status = trade.status;
    this.title_present = trade.title_present;
    this.payoff_amount = trade.payoff_amount;
    this.payoff_expiration = trade.payoff_expiration;
    this.appraised_value = trade.appraised_value;
    this.ever_location = trade.ever_location;
    this.current_location = trade.current_location;
    this.auctions = trade.auctions;
  }
}

/**
 * Audit log model
 */
export abstract class AuditLogModel {
  id: string;
  deal_id: string;
  type: 'audit' | 'comment';

  constructor(auditLog: AuditLog) {
    this.id = auditLog.id;
    this.deal_id = auditLog.deal_id;
    this.type = auditLog.type;
  }
}

/**
 * Audit model
 */
export abstract class AuditModel {
  id: string;
  deal_id: string;
  created_at: string;
  user_id: string;
  field: string;
  old_value: string;
  new_value: string;
  message: string;

  constructor(audit: Audit) {
    this.id = audit.id;
    this.deal_id = audit.deal_id;
    this.created_at = audit.created_at;
    this.user_id = audit.user_id;
    this.field = audit.field;
    this.old_value = audit.old_value;
    this.new_value = audit.new_value;
    this.message = audit.message;
  }
}

export abstract class TaskTemplateModel {
  id: string;
  @observable title: string;
  @observable description: string;
  @observable taskable_type: TaskableType;

  constructor(taskTemplate: TaskTemplate) {
    this.id = taskTemplate.id;
    this.title = taskTemplate.title;
    this.description = taskTemplate.description;
    this.taskable_type = taskTemplate.taskable_type;
  }
}

export abstract class TaskModelV2 {
  id: string;
  task_template_id?: string | null;
  taskable_id: string;
  taskable_type: TaskableType;
  @observable title: string;
  @observable description: string;
  @observable status: TaskStatus;
  @observable assignee_id?: string | null;
  @observable assignee_type?: AssigneeType | null;
  assignee?: User | null;
  @observable product_user_id?: string | null;

  constructor(task: TaskV2) {
    this.id = task.id;
    this.task_template_id = task.task_template_id;
    this.taskable_id = task.taskable_id;
    this.taskable_type = task.taskable_type;
    this.title = task.title;
    this.description = task.description;
    this.status = task.status;
    this.assignee_id = task.assignee_id;
    this.assignee_type = task.assignee_type;
    this.assignee = task.assignee;
    this.product_user_id = task.product_user_id;
  }
}

/**
 * Task model
 */
export abstract class TaskModel {
  id: string;
  title: string;
  description: string;

  constructor(task: Task) {
    this.id = task.id;
    this.title = task.title;
    this.description = task.description;
  }
}

/**
 * Assigned task model
 */
export abstract class AssignedTaskModel {
  id: string;
  task_id: string;
  deal_id: string;
  status: 'incomplete' | 'done';
  assignee_id?: string | null;
  assignee?: User | null;

  constructor(assignedTask: AssignedTask) {
    this.id = assignedTask.id;
    this.task_id = assignedTask.task_id;
    this.deal_id = assignedTask.deal_id;
    this.status = assignedTask.status;
    this.assignee_id = assignedTask.assignee_id;
    this.assignee = assignedTask.assignee;
  }
}

/**
 * Vehicle Listing Model
 */
export abstract class VehicleListingModel {
  id: string;
  vehicle_id: string;
  created_at: string;
  updated_at: string;
  @observable status: VehicleListingStatus;
  @observable purchase_status: VehicleListingPurchaseStatus;
  price: number;
  mileage: number;
  @observable num_of_keys: number;
  @observable vehicle_history: VehicleHistory;
  @observable previous_owners: number;
  @observable remaining_battery_warranty_miles: number;
  @observable remaining_battery_warranty_months: number;
  is_pre_order: boolean;
  @observable transaction_type: VehicleListing['transaction_type'];
  @observable is_listed_wholesale: boolean;

  constructor(vehicleListing: VehicleListing) {
    makeObservable(this);
    this.id = vehicleListing.id;
    this.vehicle_id = vehicleListing.vehicle_id;
    this.created_at = vehicleListing.created_at;
    this.updated_at = vehicleListing.updated_at;
    this.status = vehicleListing.status;
    this.purchase_status = vehicleListing.purchase_status;
    this.price = vehicleListing.price;
    this.mileage = vehicleListing.mileage;
    this.num_of_keys = vehicleListing.num_of_keys;
    this.vehicle_history = vehicleListing.vehicle_history;
    this.previous_owners = vehicleListing.previous_owners;
    this.is_pre_order = vehicleListing.is_pre_order;
    this.transaction_type = vehicleListing.transaction_type;
    this.is_listed_wholesale = vehicleListing.is_listed_wholesale;
    this.remaining_battery_warranty_miles =
      vehicleListing.remaining_battery_warranty_miles;
    this.remaining_battery_warranty_months =
      vehicleListing.remaining_battery_warranty_months;
  }
}

export abstract class VehicleImageModel {
  id: string;
  vehicle_listing_id: string;
  @observable image_url: string;
  @observable featured: boolean;
  @observable blur_hash_img: string;
  status: string;
  @observable order_index: number;
  @observable timestamp: number;
  @observable category: VehicleImageCategory;

  constructor(vehicleImage: VehicleImage) {
    makeObservable(this);
    this.id = vehicleImage.id;
    this.vehicle_listing_id = vehicleImage.vehicle_listing_id;
    this.image_url = vehicleImage.image_url;
    this.featured = vehicleImage.featured;
    this.blur_hash_img = vehicleImage.blur_hash_img;
    this.status = vehicleImage.status;
    this.order_index = vehicleImage.order_index;
    this.timestamp = vehicleImage.timestamp;
    this.category = vehicleImage.category;
  }
}

/**
 * Vehicle From Indexer Model
 */
export abstract class VehicleFromIndexerModel {
  id: string;
  created_at: string;
  updated_at: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  exterior_color: string;
  interior_color: string;
  @observable body: VehicleBody;
  @observable charging_plug: VehicleChargingPlug;
  @observable battery_capacity: number;
  @observable epa_range: number;
  @observable autopilot_features: string;
  @observable top_features: string;
  @observable seating_capacity: VehicleSeatingCapacity;

  constructor(vehicleFromIndexer: VehicleFromIndexer) {
    makeObservable(this);
    this.id = vehicleFromIndexer.id;
    this.created_at = vehicleFromIndexer.created_at;
    this.updated_at = vehicleFromIndexer.updated_at;
    this.vin = vehicleFromIndexer.vin;
    this.make = vehicleFromIndexer.make;
    this.model = vehicleFromIndexer.model;
    this.trim = vehicleFromIndexer.trim;
    this.year = vehicleFromIndexer.year;
    this.exterior_color = vehicleFromIndexer.exterior_color;
    this.interior_color = vehicleFromIndexer.interior_color;
    this.body = vehicleFromIndexer.body;
    this.charging_plug = vehicleFromIndexer.charging_plug;
    this.battery_capacity = vehicleFromIndexer.battery_capacity;
    this.epa_range = vehicleFromIndexer.epa_range;
    this.autopilot_features = vehicleFromIndexer.autopilot_features;
    this.top_features = vehicleFromIndexer.top_features;
    this.seating_capacity = vehicleFromIndexer.seating_capacity;
  }
}

/**
 * SMS Agent Message Model
 */
export abstract class SMSAgentMessageModel {
  id: string;
  deal_id: string;
  message_id: number;
  message_date: string;
  direction: 'inbound' | 'outbound';
  to_number: string;
  from_number: string;
  text: string;
  mms: boolean;
  mms_url: string | null;
  message_status: string;

  constructor(smsAgentMessage: SMSAgentMessage) {
    this.id = smsAgentMessage.id;
    this.deal_id = smsAgentMessage.deal_id;
    this.message_id = smsAgentMessage.message_id;
    this.message_date = smsAgentMessage.message_date;
    this.direction = smsAgentMessage.direction;
    this.to_number = smsAgentMessage.to_number;
    this.from_number = smsAgentMessage.from_number;
    this.text = smsAgentMessage.text;
    this.mms = smsAgentMessage.mms;
    this.mms_url = smsAgentMessage.mms_url;
    this.message_status = smsAgentMessage.message_status;
  }
}

export abstract class SellerModel {
  id: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  phone_number?: string;
  email?: string;

  constructor(seller: Seller) {
    this.id = String(seller.id);
    this.first_name = seller.first_name;
    this.last_name = seller.last_name;
    this.middle_name = seller.middle_name;
    this.phone_number = seller.phone_number;
    this.email = seller.email;
  }
}

export abstract class ProtectionPlanNameModel {
  id: string;
  name: string;

  constructor(protectionPlanName: ProtectionPlanName) {
    this.id = String(protectionPlanName.id);
    this.name = protectionPlanName.name;
  }
}

export abstract class ProtectionPlanTypeModel {
  id: string;
  type: string;

  constructor(protectionPlanType: ProtectionPlanType) {
    this.id = String(protectionPlanType.id);
    this.type = protectionPlanType.type;
  }
}

export abstract class ProtectionPlanTermLengthModel {
  id: string;
  term_length: number;

  constructor(protectionPlanTermLength: ProtectionPlanTermLength) {
    this.id = String(protectionPlanTermLength.id);
    this.term_length = protectionPlanTermLength.term_length;
  }
}

export abstract class ProtectionPlanTermMilesModel {
  id: string;
  term_miles: number;

  constructor(protectionPlanTermMiles: ProtectionPlanTermMiles) {
    this.id = String(protectionPlanTermMiles.id);
    this.term_miles = protectionPlanTermMiles.term_miles;
  }
}

export abstract class CustomerLeadModel {
  id: string;
  customer_id: number;
  inquiry_count: number;
  inquiries?: ConsolidatedInquiry[];
  personal_details?: PersonalDetails;
  trade_vehicles?: GenericVehicle[];
  has_ever_account: boolean;
  ever_account?: EverUser;
  previous_reservations?: PreviousReservation[];
  key_indicators?: KeyIndicators;
  original_sales_rep_id?: string;
  daily_completion_status?: string;
  priority: number;
  index?: number;

  constructor(customerLead: CustomerLead) {
    this.id = customerLead.id;
    this.customer_id = customerLead.customer_id;
    this.inquiry_count = customerLead.inquiry_count;
    this.inquiries = customerLead.inquiries;
    this.personal_details = customerLead.personal_details;
    this.trade_vehicles = customerLead.trade_vehicles;
    this.has_ever_account = customerLead.has_ever_account;
    this.ever_account = customerLead.ever_account;
    this.previous_reservations = customerLead.previous_reservations;
    this.key_indicators = customerLead.key_indicators;
    this.original_sales_rep_id = customerLead.original_sales_rep_id;
    this.daily_completion_status = customerLead.daily_completion_status;
    this.priority = customerLead.priority;
    this.index = customerLead.index;
  }
}

// V2 Model Classes with UUID IDs
export abstract class AppraisalModelV2 {
  id: string; // UUID
  tracking_id: number;
  product_user_id: string;
  deal_id?: string;
  product_marketplace_listing_id?: number;
  vin: string;
  make: string;
  model: string;
  year: number;
  zip_code: string;
  phone_number: string;
  seller_name?: string;
  email?: string;
  drivetrain: string;
  trim: string;
  body: string;
  mileage: number;
  exterior_color: string;
  interior_color: string;
  seating_capacity: number;
  num_of_keys: number;
  condition: string;
  num_of_accidents: number;
  accident_type: string;
  exterior_damage_types: string[];
  exterior_damage_description?: string | null;
  interior_damage_types: string[];
  interior_damage_description?: string | null;
  mechanical_damage_types: string[];
  mechanical_damage_description?: string | null;
  self_driving_capability: string;
  highlights: string;
  status: string;
  is_upgrade_or_purchase_interested?: boolean;
  upgrade_or_purchase_interested_info?: string;
  debt_type?: string;
  tires_condition?: string;
  exterior_panel_damage_count?: number;
  creation_step?: string;
  appraisal_origin_id?: string;
  is_serviced_regularly?: boolean;
  is_contacted_by_sales: boolean;
  sell_by?: string | null;
  is_just_browsing?: boolean | null;
  source?: string | null;
  metadata?: AppraisalMetadata | null;
  created_at: string;
  updated_at: string;

  constructor(appraisal: AppraisalV2) {
    this.id = appraisal.id;
    this.tracking_id = appraisal.tracking_id;
    this.product_user_id = String(appraisal.product_user_id);
    this.deal_id = appraisal.deal_id;
    this.product_marketplace_listing_id =
      appraisal.product_marketplace_listing_id;
    this.vin = appraisal.vin;
    this.make = appraisal.make;
    this.model = appraisal.model;
    this.year = appraisal.year;
    this.zip_code = appraisal.zip_code;
    this.phone_number = appraisal.phone_number;
    this.seller_name = appraisal.seller_name;
    this.email = appraisal.email;
    this.drivetrain = appraisal.drivetrain;
    this.trim = appraisal.trim;
    this.body = appraisal.body;
    this.mileage = appraisal.mileage;
    this.exterior_color = appraisal.exterior_color;
    this.interior_color = appraisal.interior_color;
    this.seating_capacity = appraisal.seating_capacity;
    this.num_of_keys = appraisal.num_of_keys;
    this.condition = appraisal.condition;
    this.num_of_accidents = appraisal.num_of_accidents;
    this.accident_type = appraisal.accident_type;
    this.exterior_damage_types = appraisal.exterior_damage_types;
    this.exterior_damage_description = appraisal.exterior_damage_description;
    this.interior_damage_types = appraisal.interior_damage_types;
    this.interior_damage_description = appraisal.interior_damage_description;
    this.mechanical_damage_types = appraisal.mechanical_damage_types;
    this.mechanical_damage_description =
      appraisal.mechanical_damage_description;
    this.self_driving_capability = appraisal.self_driving_capability;
    this.highlights = appraisal.highlights;
    this.status = appraisal.status;
    this.is_upgrade_or_purchase_interested =
      appraisal.is_upgrade_or_purchase_interested;
    this.upgrade_or_purchase_interested_info =
      appraisal.upgrade_or_purchase_interested_info;
    this.debt_type = appraisal.debt_type;
    this.tires_condition = appraisal.tires_condition;
    this.exterior_panel_damage_count = appraisal.exterior_panel_damage_count;
    this.creation_step = appraisal.creation_step;
    this.appraisal_origin_id = appraisal.appraisal_origin_id;
    this.is_serviced_regularly = appraisal.is_serviced_regularly;
    this.is_contacted_by_sales = appraisal.is_contacted_by_sales;
    this.sell_by = appraisal.sell_by;
    this.is_just_browsing = appraisal.is_just_browsing;
    this.source = appraisal.source;
    this.metadata = appraisal.metadata;
    this.created_at = appraisal.created_at;
    this.updated_at = appraisal.updated_at;
  }
}

export abstract class AppraisalOfferModelV2 {
  id: string; // UUID
  tracking_id: number;
  offer_type: string;
  offer_amount_dollars: number;
  appraisal_v2_id: string; // UUID
  status: string;
  expires_at: string;
  created_at: string;
  updated_at: string;

  constructor(offer: AppraisalOfferV2) {
    this.id = offer.id;
    this.tracking_id = offer.tracking_id;
    this.offer_type = offer.offer_type;
    this.offer_amount_dollars = offer.offer_amount_dollars;
    this.appraisal_v2_id = offer.appraisal_v2_id;
    this.status = offer.status;
    this.expires_at = offer.expires_at;
    this.created_at = offer.created_at;
    this.updated_at = offer.updated_at;
  }
}

export abstract class InstantOfferModelV2 {
  id: string; // UUID
  tracking_id: number;
  channel: string;
  channel_id: string;
  state: string;
  status: string;
  offer_type: string;
  powertrain_type: string;
  margin: number;
  risk_adjustment: number;
  risk_level: string;
  max_bid_price: number | null;
  start_bid_price: number | null;
  sales_price_estimate: number | null;
  sales_price_estimate_source: string | null;
  is_upgrade_or_purchase_interested: boolean | null;

  constructor(instantOffer: InstantOfferV2) {
    this.id = instantOffer.id;
    this.tracking_id = instantOffer.tracking_id;
    this.channel = instantOffer.channel;
    this.channel_id = instantOffer.channel_id;
    this.state = instantOffer.state;
    this.status = instantOffer.status;
    this.offer_type = instantOffer.offer_type;
    this.powertrain_type = instantOffer.powertrain_type;
    this.margin = instantOffer.margin;
    this.risk_adjustment = instantOffer.risk_adjustment;
    this.risk_level = instantOffer.risk_level;
    this.max_bid_price = instantOffer.max_bid_price;
    this.start_bid_price = instantOffer.start_bid_price;
    this.sales_price_estimate = instantOffer.sales_price_estimate;
    this.sales_price_estimate_source = instantOffer.sales_price_estimate_source;
    this.is_upgrade_or_purchase_interested =
      instantOffer.is_upgrade_or_purchase_interested;
  }
}
